import React, { useState } from 'react';
import { Home } from 'lucide-react';
import sendEngineeringEmail from './engineeringWordService';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';

const EngineeringForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    engineerName: '',
    jobLocation: '',
    companyRepresenting: '',
    jobDescription: '',
    partsUsed: '',
    timeOnSite: '',
    workComplete: '',
    reasonForIncomplete: '',
    dateCompleted: '',
    startTime: '',
    endTime: '',
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showYesBlockedMessage, setShowYesBlockedMessage] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'workComplete') {
      if (value === 'yes' && formData.reasonForIncomplete.trim()) {
        setShowYesBlockedMessage(true);
        return;
      }
      if (value === 'yes') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          reasonForIncomplete: ''
        }));
        setShowYesBlockedMessage(false);
        return;
      }
      if (value === 'no') {
        setShowYesBlockedMessage(false);
      }
    }
    
    if (name === 'reasonForIncomplete') {
      setShowYesBlockedMessage(false);
      if (value.trim()) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          workComplete: 'no'
        }));
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await sendEngineeringEmail(formData);
      setFormData({
        engineerName: '',
        jobLocation: '',
        companyRepresenting: '',
        jobDescription: '',
        partsUsed: '',
        timeOnSite: '',
        workComplete: '',
        reasonForIncomplete: '',
        dateCompleted: '',
        startTime: '',
        endTime: '',
        notes: ''
      });
      setShowYesBlockedMessage(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting engineering form:', error);
      setSubmitMessage('Error submitting engineering form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Engineering Form</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-ENG-001 | Version: 1.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Complete Engineering Documentation</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-white mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Engineer Name *</label>
                  <input type="text" name="engineerName" value={formData.engineerName} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Job Location *</label>
                  <input type="text" name="jobLocation" value={formData.jobLocation} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Company Representing *</label>
                  <input type="text" name="companyRepresenting" value={formData.companyRepresenting} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date Completed *</label>
                  <input type="date" name="dateCompleted" value={formData.dateCompleted} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
              </div>
            </div>

            {/* Job Details Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-white mb-4">Job Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Job Description *</label>
                  <textarea name="jobDescription" value={formData.jobDescription} onChange={handleInputChange} rows={4} className="block w-full pvs-input" placeholder="Describe the work performed in detail..." required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Parts Used</label>
                  <textarea name="partsUsed" value={formData.partsUsed} onChange={handleInputChange} rows={3} className="block w-full pvs-input" placeholder="List all parts, materials, and components used..." />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Time on Site *</label>
                    <input type="text" name="timeOnSite" value={formData.timeOnSite} onChange={handleInputChange} className="block w-full pvs-input" placeholder="e.g., 4 hours, 2 days" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Start Time</label>
                    <input type="time" name="startTime" value={formData.startTime} onChange={handleInputChange} className="block w-full pvs-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">End Time</label>
                    <input type="time" name="endTime" value={formData.endTime} onChange={handleInputChange} className="block w-full pvs-input" />
                  </div>
                </div>
              </div>
            </div>

            {/* Work Completion Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-white mb-4">Work Status</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-3">Work Complete *</label>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center">
                      <input type="radio" name="workComplete" value="yes" checked={formData.workComplete === 'yes'} onChange={handleInputChange} disabled={formData.reasonForIncomplete.trim() !== ''} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border" />
                      <label className="ml-2 text-sm text-pvs-text-muted">Yes</label>
                    </div>
                    <div className="flex items-center">
                      <input type="radio" name="workComplete" value="no" checked={formData.workComplete === 'no'} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border" />
                      <label className="ml-2 text-sm text-pvs-text-muted">No</label>
                    </div>
                  </div>
                </div>

                {formData.workComplete === 'no' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Reason for work not being completed *</label>
                    <textarea name="reasonForIncomplete" value={formData.reasonForIncomplete} onChange={handleInputChange} rows={3} className="block w-full pvs-input" placeholder="Please explain why the work was not completed..." required={formData.workComplete === 'no'} />
                  </div>
                )}

                {formData.workComplete === 'no' && (
                  <div className="mt-4 p-4 bg-amber-900/20 border border-amber-500/30 rounded-md">
                    <p className="text-amber-400 text-sm">
                      ⚠️ Work is marked as incomplete. Please provide a reason above.
                    </p>
                  </div>
                )}

                {showYesBlockedMessage && (
                  <div className="mt-4 p-4 bg-pvs-accent/5 border border-pvs-accent/20 rounded-md shadow-pvs-inner">
                    <p className="text-pvs-accent text-sm">
                      ℹ️ You cannot select "Yes" while there is text in the reason field. Clear the reason to mark work as complete.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Notes Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-white mb-4">Additional Notes</h2>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={4} className="block w-full pvs-input" placeholder="Any additional notes, observations, or recommendations..." />
              </div>
            </div>

            {submitMessage && (
              <div className="p-4 rounded-md bg-red-900/30 border border-red-500/30 text-red-400">
                {submitMessage}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50"
              >
                Back to Forms
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50 transition-all duration-200"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Engineering Form'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <FormSuccessModal isOpen={showSuccessModal} formName="Engineering Form" onClose={onBack} />
    </PVSPageLayout>
  );
};

export default EngineeringForm;
